import React from 'react';
import Dashboard from '../../components/Dashboard';
import CustomerList from '../../components/CustomerList';
import CustomerDetails from '../../components/CustomerDetails';
import JobList from '../../components/JobList';
import JobDetails from '../../components/JobDetails';
import InvoiceList from '../../components/InvoiceList';
import TreatmentReportList from '../../components/TreatmentReportList';
import Calendar from '../../components/Calendar';
import AppointmentList from '../../components/AppointmentList';
import Settings from '../../components/Settings';
import UserManagement from '../../components/UserManagement';
import CanvassingData from '../../components/CanvassingData';
import CanvassingMobile from '../../components/CanvassingMobile';
import EmailList from '../../components/EmailList';
import ReminderList from '../../components/ReminderList';
import { VIEWS, USER_ROLES } from '../utils/appConstants';

const AppRouter = ({ 
  currentView, 
  navigation,
  appData,
  userProfile,
  user,
  modalActions,
  crudActions
}) => {
  const {
    selectedCustomer,
    selectedJob,
    selectedTreatmentReport,
    preselectedCustomer,
    previousView,
    navigationContext,
    handleNavigation,
    goToPreviousView,
    setPreselectedCustomer
  } = navigation;
  
  const { customers, jobs, customerCount } = appData;

  switch (currentView) {
    case VIEWS.DASHBOARD:
      return (
        <Dashboard
          jobs={jobs}
          onNavigate={handleNavigation}
          user={user}
          userProfile={userProfile}
          onAddJob={() => modalActions.openCustomerSearchModal('job')}
          onAddContract={() => modalActions.openCustomerSearchModal('contract')}
        />
      );

    case VIEWS.CUSTOMERS:
      return (
        <CustomerList
          onCustomerSelect={(customer) => handleNavigation(VIEWS.CUSTOMER_DETAILS, customer)}
          onAddCustomer={modalActions.openAddCustomerModal}
          currentUser={user}
          userProfile={userProfile}
          onAddJob={() => modalActions.openCustomerSearchModal('job')}
          onAddContract={() => modalActions.openCustomerSearchModal('contract')}
        />
      );
      
    case VIEWS.CUSTOMER_DETAILS:
      return (
        <CustomerDetails
          customer={selectedCustomer}
          onUpdateCustomer={crudActions.updateCustomer}
          onDeleteCustomer={crudActions.deleteCustomer}

          onJobSelect={(job) => handleNavigation(VIEWS.JOB_DETAILS, job)}
          onNavigate={handleNavigation}
          user={user}
          userProfile={userProfile}
          onRefreshData={appData.refreshData}
        />
      );
      
    case VIEWS.JOBS:
      return (
        <JobList
          jobs={jobs}
          customers={customers}
          onJobSelect={(job) => handleNavigation(VIEWS.JOB_DETAILS, job)}
          onAddJob={() => modalActions.openCustomerSearchModal('job')}
          onAddContract={() => modalActions.openCustomerSearchModal('contract')}
          user={user}
          userProfile={userProfile}
          onRefreshData={appData.refreshData}
          initialFilter={navigationContext?.initialFilter}
        />
      );
      
    case VIEWS.JOB_DETAILS:
      // Create customer object from job's customer data (from backend JOIN)
      // Fallback to looking up in customers array if needed
      const jobCustomer = selectedJob ? {
        id: selectedJob.customer_id,
        name: selectedJob.customer_name,
        email: selectedJob.customer_email,
        phone: selectedJob.customer_phone,
        address: selectedJob.customer_address,
        company_name: selectedJob.customer_company
      } : customers.find(c => c.id === selectedJob?.customer_id);

      return (
        <JobDetails
          job={selectedJob}
          customer={jobCustomer}
          user={user}
          userProfile={userProfile}
          navigationContext={navigationContext}
          onUpdateJob={crudActions.updateJob}
          onDeleteJob={(jobId) => {
            crudActions.deleteJob(jobId);
            handleNavigation(VIEWS.JOBS);
          }}
          onEditJob={(job) => modalActions.openAddJobModal(job)}
          onAddAppointment={modalActions.openAddAppointmentModal}
          appointmentVersion={modalActions.appointmentVersion}
          onNavigateToCustomer={(customer) => handleNavigation(VIEWS.CUSTOMER_DETAILS, customer)}
          onNavigate={handleNavigation}

        />
      );
      
    case VIEWS.INVOICES:
      return <InvoiceList
        userProfile={userProfile}
        preselectedCustomer={preselectedCustomer}
        previousView={previousView}
        onClearPreselectedCustomer={() => setPreselectedCustomer(null)}
        onGoToPreviousView={goToPreviousView}
        onCustomerSelect={(customer) => handleNavigation(VIEWS.CUSTOMER_DETAILS, customer)}
        onJobSelect={(job, context) => handleNavigation(VIEWS.JOB_DETAILS, job, context)}
        onAddJob={() => modalActions.openCustomerSearchModal('job')}
        onAddContract={() => modalActions.openCustomerSearchModal('contract')}
        onAddJobForCustomer={(customer) => modalActions.openAddJobModalForCustomer(customer)}
      />;
      
    case VIEWS.TREATMENT_REPORTS:
      return (
        <TreatmentReportList
          onReportSelect={(report) => handleNavigation(VIEWS.TREATMENT_REPORT_DETAILS, report)}
          onAddReport={modalActions.openTreatmentReportEditor}
          onEditReport={(report) => modalActions.openTreatmentReportEditor(report)}
          currentUser={user}
          userProfile={userProfile}
        />
      );
      
    case VIEWS.TREATMENT_REPORT_DETAILS:
      return (
        <div className="p-6">
          <div className="text-center py-12">
            <p className="text-gray-500">Treatment Report Details View - Coming Soon</p>
            <p className="text-sm text-gray-400 mt-2">
              Report: {selectedTreatmentReport?.report_number}
            </p>
          </div>
        </div>
      );

    case VIEWS.CALENDAR:
      return (
        <Calendar
          userProfile={userProfile}
          onAddAppointment={modalActions.openAddAppointmentModal}
          onEditAppointment={modalActions.openEditAppointmentModal}
        />
      );

    case VIEWS.APPOINTMENTS:
      return (
        <AppointmentList
          user={user}
          userProfile={userProfile}
          onNavigate={handleNavigation}
          initialFilter={navigationContext?.initialFilter}
          onAddJob={() => modalActions.openCustomerSearchModal('job')}
          onAddContract={() => modalActions.openCustomerSearchModal('contract')}
        />
      );

    case VIEWS.CANVASSING:
      return userProfile?.role === USER_ROLES.ADMIN ? (
        <CanvassingData userProfile={userProfile} />
      ) : (
        <div className="p-6">
          <div className="text-center py-12">
            <p className="text-red-500">Access denied. Admin privileges required.</p>
          </div>
        </div>
      );

    case VIEWS.CANVASSING_MOBILE:
      return (userProfile?.role === USER_ROLES.FRANCHISEE || userProfile?.role === USER_ROLES.TECHNICIAN) ? (
        <CanvassingMobile userProfile={userProfile} user={user} />
      ) : (
        <div className="p-6">
          <div className="text-center py-12">
            <p className="text-red-500">Access denied. Franchisee or Technician role required.</p>
          </div>
        </div>
      );

    case VIEWS.REMINDERS:
      return (
        <ReminderList
          userProfile={userProfile}
          user={user}
          onNavigate={handleNavigation}
          onAddJob={() => modalActions.openCustomerSearchModal('job')}
          onAddContract={() => modalActions.openCustomerSearchModal('contract')}
        />
      );

    case VIEWS.EMAILS:
      return <EmailList
        userProfile={userProfile}
        onNavigate={handleNavigation}
        initialSearch={navigationContext?.emailSearch || ''}
        onAddJob={() => modalActions.openCustomerSearchModal('job')}
        onAddContract={() => modalActions.openCustomerSearchModal('contract')}
      />;

    case VIEWS.SETTINGS:
      return <Settings userProfile={userProfile} />;
      
    case VIEWS.USERS:
      return userProfile?.role === USER_ROLES.ADMIN ? (
        <UserManagement currentUser={user} userProfile={userProfile} />
      ) : userProfile?.role === USER_ROLES.FRANCHISEE ? (
        <UserManagement currentUser={user} userProfile={userProfile} />
      ) : (
        <div className="p-6">
          <div className="text-center py-12">
            <p className="text-red-500">Access denied. Admin or Franchisee privileges required.</p>
          </div>
        </div>
      );
      
    default:
      return (
        <Dashboard
          jobs={jobs}
          onNavigate={handleNavigation}
          user={user}
          userProfile={userProfile}
          onAddJob={() => modalActions.openCustomerSearchModal('job')}
          onAddContract={() => modalActions.openCustomerSearchModal('contract')}
        />
      );
  }
};

export default AppRouter;